// components/AppMetricsCard.tsx
import React, { useState } from 'react';
import { QueryRenderer } from '@cubejs-client/react';
import { CubejsApi } from '@cubejs-client/core';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CloseIcon from '@mui/icons-material/Close';

interface AppMetricsCardProps {
    title: string;
    platform: 'ios' | 'android';
    cubeName: 'AthleteAppUsers' | 'CoachAppUsers';
    cubejsApi: CubejsApi;
}

interface UserDrilldownModalProps {
    open: boolean;
    onClose: () => void;
    version: string;
    platform: 'ios' | 'android';
    cubeName: string;
    cubejsApi: CubejsApi;
}

const UserDrilldownModal: React.FC<UserDrilldownModalProps> = ({
    open,
    onClose,
    version,
    platform,
    cubeName,
    cubejsApi,
}) => {
    const osFilter = platform === 'ios' ? 'iOS' : 'Android';

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="user-drilldown-modal"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #4a3f35',
                    borderRadius: '8px',
                    width: '95%',
                    maxWidth: '1500px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: '1px solid #4a3f35',
                        backgroundColor: '#3d3630',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {platform === 'ios' ? (
                            <AppleIcon sx={{ mr: 1, color: '#fff' }} />
                        ) : (
                            <AndroidIcon sx={{ mr: 1, color: '#FFD700' }} />
                        )}
                        <Typography variant="h6" sx={{ color: '#fff' }}>
                            Users on v{version}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: '#a0a0a0' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <QueryRenderer
                    cubejsApi={cubejsApi}
                    query={{
                        dimensions: [
                            `${cubeName}.email`,
                            `${cubeName}.phone`,
                            `${cubeName}.firstName`,
                            `${cubeName}.lastName`,
                            `${cubeName}.userName`,
                            `${cubeName}.deviceModel`,
                            `${cubeName}.osVersion`,
                            `${cubeName}.receivedAt`,
                        ],
                        timeDimensions: [
                            {
                                dimension: `${cubeName}.timestamp`,
                                dateRange: 'Last 30 days',
                            },
                        ],
                        filters: [
                            {
                                member: `${cubeName}.appVersion`,
                                operator: 'equals',
                                values: [version],
                            },
                            {
                                member: `${cubeName}.osName`,
                                operator: 'equals',
                                values: [osFilter],
                            },
                        ],
                        order: {
                            [`${cubeName}.receivedAt`]: 'desc',
                        },
                        limit: 1000,
                    }}
                    render={({ resultSet, loadingState, error }) => {
                        const isLoading = loadingState?.isLoading ?? !resultSet;

                        if (isLoading && !error) {
                            return (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight={300}
                                >
                                    <CircularProgress sx={{ color: '#a08060' }} />
                                </Box>
                            );
                        }

                        if (error) {
                            return (
                                <Box sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#ff6b6b' }}>
                                        Error: {error.toString()}
                                    </Typography>
                                </Box>
                            );
                        }

                        if (!resultSet) {
                            return null;
                        }

                        const data = resultSet.tablePivot();

                        const formatDate = (dateString: string) => {
                            if (!dateString) return '-';
                            const date = new Date(dateString);
                            return date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            });
                        };

                        return (
                            <>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        backgroundColor: '#1a1a1a',
                                    }}
                                >
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Email
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Phone
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    First Name
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Last Name
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Username
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Device
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    OS Version
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#a0a0a0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Last Seen
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={7}
                                                        sx={{
                                                            color: '#6b5d4f',
                                                            textAlign: 'center',
                                                            py: 4,
                                                        }}
                                                    >
                                                        No users found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                data.map((row, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            '&:nth-of-type(odd)': {
                                                                backgroundColor: '#222',
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: '#333',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                                            {row[`${cubeName}.email`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                                            {row[`${cubeName}.phone`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                                            {row[`${cubeName}.firstName`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                                            {row[`${cubeName}.lastName`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#d0d0d0' }}>
                                                            {row[`${cubeName}.userName`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#a0a0a0' }}>
                                                            {row[`${cubeName}.deviceModel`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#a0a0a0' }}>
                                                            {row[`${cubeName}.osVersion`] || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#a0a0a0' }}>
                                                            {formatDate(
                                                                row[
                                                                    `${cubeName}.receivedAt`
                                                                ] as string,
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box
                                    sx={{
                                        p: 2,
                                        borderTop: '1px solid #4a3f35',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: '#6b5d4f' }}>
                                        Showing {data.length} users • Last 30 days
                                    </Typography>
                                </Box>
                            </>
                        );
                    }}
                />
            </Box>
        </Modal>
    );
};

const AppMetricsCard: React.FC<AppMetricsCardProps> = ({
    title,
    platform,
    cubeName,
    cubejsApi,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState<string>('');

    const osFilter = platform === 'ios' ? 'iOS' : 'Android';

    const handleVersionClick = (version: string) => {
        setSelectedVersion(version);
        setModalOpen(true);
    };

    return (
        <>
            <QueryRenderer
                cubejsApi={cubejsApi}
                query={{
                    measures: [`${cubeName}.uniqueUsers`],
                    dimensions: [`${cubeName}.appVersion`],
                    timeDimensions: [
                        {
                            dimension: `${cubeName}.timestamp`,
                            dateRange: 'Last 30 days',
                        },
                    ],
                    filters: [
                        {
                            member: `${cubeName}.appVersion`,
                            operator: 'set',
                        },
                        {
                            member: `${cubeName}.osName`,
                            operator: 'equals',
                            values: [osFilter],
                        },
                    ],
                    order: {
                        [`${cubeName}.uniqueUsers`]: 'desc',
                    },
                }}
                render={({ resultSet, loadingState, error }) => {
                    const isLoading = loadingState?.isLoading ?? !resultSet;

                    if (isLoading && !error) {
                        return (
                            <Card
                                sx={{
                                    height: '100%',
                                    minHeight: 250,
                                    borderRadius: '8px',
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid #4a3f35',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#3d3630',
                                        padding: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {platform === 'ios' ? (
                                        <AppleIcon sx={{ mr: 1, color: '#fff' }} />
                                    ) : (
                                        <AndroidIcon sx={{ mr: 1, color: '#FFD700' }} />
                                    )}
                                    <Typography variant="h6" sx={{ color: '#fff' }}>
                                        {title}
                                    </Typography>
                                </Box>
                                <CardContent>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        minHeight={150}
                                    >
                                        <CircularProgress sx={{ color: '#a08060' }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    }

                    if (error) {
                        return (
                            <Card
                                sx={{
                                    height: '100%',
                                    minHeight: 250,
                                    borderRadius: '8px',
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid #4a3f35',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#3d3630',
                                        padding: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {platform === 'ios' ? (
                                        <AppleIcon sx={{ mr: 1, color: '#fff' }} />
                                    ) : (
                                        <AndroidIcon sx={{ mr: 1, color: '#FFD700' }} />
                                    )}
                                    <Typography variant="h6" sx={{ color: '#fff' }}>
                                        {title}
                                    </Typography>
                                </Box>
                                <CardContent>
                                    <Typography sx={{ color: '#ff6b6b' }}>
                                        Error: {error.toString()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    }

                    if (!resultSet) {
                        return null;
                    }

                    const data = resultSet.tablePivot();
                    const totalUsers = data.reduce(
                        (sum, row) => sum + Number(row[`${cubeName}.uniqueUsers`] || 0),
                        0,
                    );

                    const versionData = data
                        .map((row) => ({
                            version: row[`${cubeName}.appVersion`] as string,
                            count: Number(row[`${cubeName}.uniqueUsers`] || 0),
                        }))
                        .filter((v) => v.version && v.count > 0)
                        .sort((a, b) => b.count - a.count);

                    return (
                        <Card
                            sx={{
                                height: '100%',
                                minHeight: 250,
                                borderRadius: '8px',
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #4a3f35',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: '#3d3630',
                                    padding: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {platform === 'ios' ? (
                                    <AppleIcon sx={{ mr: 1, fontSize: 28, color: '#fff' }} />
                                ) : (
                                    <AndroidIcon sx={{ mr: 1, fontSize: 28, color: '#FFD700' }} />
                                )}
                                <Typography variant="h6" sx={{ color: '#fff' }}>
                                    {title}
                                </Typography>
                            </Box>

                            <CardContent>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                        Total Users (Last 30 Days)
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ fontWeight: 'bold', color: '#a08060' }}
                                    >
                                        {totalUsers.toLocaleString()}
                                    </Typography>
                                </Box>

                                {versionData.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: '#a0a0a0' }}
                                        >
                                            By Version{' '}
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{ color: '#6b5d4f' }}
                                            >
                                                (click to view users)
                                            </Typography>
                                        </Typography>
                                        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                                            {versionData.map(({ version, count }) => (
                                                <Box
                                                    key={version}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    onClick={() => handleVersionClick(version)}
                                                    sx={{
                                                        py: 0.5,
                                                        px: 1,
                                                        mx: -1,
                                                        borderRadius: 1,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: '#2a2a2a',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            color: '#d0d0d0',
                                                        }}
                                                    >
                                                        v{version}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 'bold', color: '#fff' }}
                                                    >
                                                        {count.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    );
                }}
            />

            <UserDrilldownModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                version={selectedVersion}
                platform={platform}
                cubeName={cubeName}
                cubejsApi={cubejsApi}
            />
        </>
    );
};

export default AppMetricsCard;
